import SEO from "../components/SEO";
import seoPages from "../data/seoPages.json";
import React from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CoursesSection } from "../components/CoursesSection";
import { 
  Mail, 
  FileText, 
  LogIn, 
  BookOpen, 
  MessageCircle,
  CheckCircle2,
  PartyPopper,
  Send
} from "lucide-react";
import { Button } from "../components/ui/button";

const instructionSteps = [
  {
    step: 1,
    title: "Проверка почты",
    description: "После успешной оплаты вы получите письмо на почту, указанную при оплате.",
    icon: Mail,
    tip: "Письмо придёт в течение нескольких минут"
  },
  {
    step: 2,
    title: "Содержимое письма",
    description: "Ознакомьтесь с содержимым письма – проверьте все папки, включая «Спам».",
    icon: FileText,
    tip: "В письме будет ссылка для входа на платформу"
  },
  {
    step: 3,
    title: "Вход в аккаунт",
    description: "Нажмите на кнопку «Войти в аккаунт» и создайте пароль, используя вашу почту как логин.",
    icon: LogIn,
    tip: "Используйте надёжный пароль"
  },
  {
    step: 4,
    title: "Доступ к курсам",
    description: "После входа вы увидите список доступных курсов. Для удобства используйте одну и ту же почту при каждой покупке.",
    icon: BookOpen,
    tip: "Доступ к курсам открывается сразу"
  }
];

export const PaymentSuccessPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...seoPages.paymentSuccess} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#F1EDE4] to-[#E6E0D3]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-teal-500/20 rounded-full mb-6">
            <PartyPopper className="w-10 h-10 text-teal-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Спасибо за покупку!
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Ниже вы найдёте пошаговую инструкцию по доступу к материалам курса
          </p>
        </div>
      </section>

      {/* Instructions Grid */}
      <section className="py-16 -mt-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {instructionSteps.map((item) => (
              <div 
                key={item.step}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-teal-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded">
                        Шаг {item.step}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-3">
                      {item.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-teal-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>{item.tip}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Problems Card */}
            <div className="bg-[#F1EDE4] rounded-2xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/20 rounded-full mb-4">
                <MessageCircle className="w-8 h-8 text-teal-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Проблемы с доступом?
              </h2>
              <p className="text-gray-400 mb-6">
                Если возникли сложности, напишите нам, указав вашу почту и приложив чек или скриншот оплаты.
              </p>
              <a
                href="https://t.me/DmitriiFokeev"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#0088cc] hover:bg-[#0077b5] text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all">
                  <Send className="w-5 h-5 mr-2" />
                  Написать в Telegram
                </Button>
              </a>
            </div>

            {/* Telegram Channel Card */}
            <div className="bg-[#F1EDE4] rounded-2xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/20 rounded-full mb-4">
                <Send className="w-8 h-8 text-teal-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Присоединяйтесь к нам!
              </h2>
              <p className="text-gray-400 mb-6">
                Подписывайтесь на наш Telegram-канал, чтобы быть в курсе новых курсов, акций и полезных материалов.
              </p>
              <a
                href="https://t.me/DmitriiFokeevEducation"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#0088cc] hover:bg-[#0077b5] text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all">
                  <Send className="w-5 h-5 mr-2" />
                  Наш Telegram-канал
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Courses Link */}
      <section className="py-8">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Link to="/courses" className="course-catalog-link">
            <span className="course-catalog-text">Вернуться к курсам</span><span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      {/* Courses Section */}
      <CoursesSection />

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
